import type { EntityState } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import type { Loadable } from '../../models/loadable.model';
import { emptyLoadable } from '../../models/loadable.model';
import type { NeedAccessResponse } from '../../need-access/need-access.interface';
import type {
  AccessResponse,
  VideoAccessResponse,
  VideoListAccessResponse,
} from '../models/access-response';
import type { Collectible } from '../models/collectibles.interface';
import type { PromotedProductsResponse } from '../models/promoted-products.interface';
import {
  buyItNow,
  loadCollectibles,
  loadNeedAccess,
  loadProductAccess,
  loadPromotedProducts,
  loadVideo,
  loadVideoBar,
} from './content.thunk';

const videoAdapter = createEntityAdapter<VideoAccessResponse>();
export const videoAdapterSelectors = videoAdapter.getSelectors();

const videoBarsAdapter = createEntityAdapter<VideoListAccessResponse>();
export const videoBarsAdapterSelectors = videoBarsAdapter.getSelectors();

export interface IContentState {
  videos: Loadable<EntityState<VideoAccessResponse, string>>;
  videoBars: Loadable<EntityState<VideoListAccessResponse, string>>;
  productAccess: Loadable<AccessResponse>;
  needAccess: Loadable<NeedAccessResponse>;
  promotedProducts: Loadable<PromotedProductsResponse>;
  addingToCart: Loadable<any>;
  buyItNow: Loadable<any>;
  collectibles: Loadable<Collectible[]>;
}

const initialState: IContentState = {
  videos: {
    value: videoAdapter.getInitialState(),
    loaded: false,
    loading: false,
    error: undefined,
  },
  videoBars: {
    value: videoBarsAdapter.getInitialState(),
    loaded: false,
    loading: false,
    error: undefined,
  },
  productAccess: emptyLoadable(),
  needAccess: emptyLoadable(),
  promotedProducts: emptyLoadable(),
  addingToCart: emptyLoadable(),
  buyItNow: emptyLoadable(),
  collectibles: emptyLoadable(),
};

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    resetContent: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadVideo.pending, (state: IContentState) => {
        state.videos.loading = true;
        state.videos.error = undefined;
      })
      .addCase(loadVideo.fulfilled, (state: IContentState, action) => {
        state.videos.loading = false;
        state.videos.loaded = true;
        state.videos.error = undefined;
        videoAdapter.upsertOne(state.videos.value!, action.payload);
      })
      .addCase(loadVideo.rejected, (state: IContentState, action) => {
        state.videos.loading = false;
        state.videos.loaded = false;
        state.videos.error = action.payload;
      });
    builder
      .addCase(loadVideoBar.pending, (state: IContentState) => {
        state.videoBars.loading = true;
        state.videoBars.error = undefined;
      })
      .addCase(loadVideoBar.fulfilled, (state: IContentState, action) => {
        state.videoBars.loading = false;
        state.videoBars.loaded = true;
        state.videoBars.error = undefined;
        videoBarsAdapter.upsertOne(state.videoBars.value!, action.payload);
      })
      .addCase(loadVideoBar.rejected, (state: IContentState, action) => {
        state.videoBars.loading = false;
        state.videoBars.loaded = false;
        state.videoBars.error = action.payload;
      });
    builder
      .addCase(loadProductAccess.pending, (state: IContentState) => {
        state.productAccess.loading = true;
        state.productAccess.error = undefined;
      })
      .addCase(loadProductAccess.fulfilled, (state: IContentState, action) => {
        state.productAccess.loading = false;
        state.productAccess.loaded = true;
        state.productAccess.value = action.payload;
      })
      .addCase(loadProductAccess.rejected, (state: IContentState, action) => {
        state.productAccess.loading = false;
        state.productAccess.loaded = false;
        state.productAccess.error = action.payload;
      });
    builder
      .addCase(loadNeedAccess.pending, (state: IContentState) => {
        state.needAccess.loading = true;
        state.needAccess.error = undefined;
      })
      .addCase(loadNeedAccess.fulfilled, (state: IContentState, action) => {
        state.needAccess.loading = false;
        state.needAccess.loaded = true;
        state.needAccess.value = action.payload;
      })
      .addCase(loadNeedAccess.rejected, (state: IContentState, action) => {
        state.needAccess.loading = false;
        state.needAccess.loaded = false;
        state.needAccess.error = action.payload;
      });
    builder
      .addCase(loadPromotedProducts.pending, (state: IContentState) => {
        state.promotedProducts.loading = true;
        state.promotedProducts.error = undefined;
      })
      .addCase(loadPromotedProducts.fulfilled, (state: IContentState, action) => {
        state.promotedProducts.loading = false;
        state.promotedProducts.loaded = true;
        state.promotedProducts.value = action.payload;
      })
      .addCase(loadPromotedProducts.rejected, (state: IContentState, action) => {
        state.promotedProducts.loading = false;
        state.promotedProducts.loaded = false;
        state.promotedProducts.error = action.payload;
      });
    builder
      .addCase(buyItNow.pending, (state: IContentState) => {
        state.buyItNow.loading = true;
        state.buyItNow.error = undefined;
      })
      .addCase(buyItNow.fulfilled, (state: IContentState, action) => {
        state.buyItNow.loading = false;
        state.buyItNow.loaded = true;
      })
      .addCase(buyItNow.rejected, (state: IContentState, action) => {
        state.buyItNow.loading = false;
        state.buyItNow.loaded = false;
        state.buyItNow.error = action.payload;
      });
    builder
      .addCase(loadCollectibles.pending, (state: IContentState) => {
        state.collectibles.loading = true;
        state.collectibles.error = undefined;
      })
      .addCase(loadCollectibles.fulfilled, (state: IContentState, action) => {
        state.collectibles.loading = false;
        state.collectibles.loaded = true;
        state.collectibles.value = action.payload;
      })
      .addCase(loadCollectibles.rejected, (state: IContentState, action) => {
        state.collectibles.loading = false;
        state.collectibles.loaded = false;
        state.collectibles.error = action.payload;
      });
  },
});

export const { resetContent } = contentSlice.actions;
export const contentReducer = contentSlice.reducer;
